import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDisplayViewport } from '@chewy/kib-helpers-react';
import '@chewy/kib-divider-styles/dist/kib-divider.css';
import { useLocation } from 'react-router';
import { PetInterface } from 'types/pet';
import { Helmet } from 'react-helmet-async';
import { getPetClinics } from '../../api/vetClinicApi';
import { getMicroInteraction } from '../../api/mintApi';
import Overview from '../../components/overview/Overview';
import Banner from '../../components/banner/Banner';
import FetchError from '../../types/http';
import { retryCount } from '../../util/reactQueryOptions';
import { ApiPetPrescription } from '../../api/generated-client/PetProfile-Service-A.v1/models';
import PetProfileClinics from '../../components/pet-profile-clinics/PetProfileClinics';
import PetProfileDetails from '../../components/pet-profile-details/PetProfileDetails';
import { RenderArticles } from '../../components/articles/RenderArticles';
import EditActionChip from '../../components/edit-profile-button/EditProfileButton';
import { Clinics } from '../../components/pet-form/VetClinic/vetClinicResponse';
import { isNotChewyMobileApp } from '../../util/helpers';
import { PetViewContent } from '../pet-view-content/PetViewContent';
import { ProfileBreadCrumbs } from './ProfileBreadCrumbs';
import './petProfileView.scss';

const PetProfileView = ({
  petProfile: petProfileData,
  petPrescriptions,
}: {
  petProfile: PetInterface;
  petPrescriptions?: ApiPetPrescription[];
}) => {
  const [{ minLarge }, setBreakpoints] = useState<{ minLarge: boolean }>({ minLarge: true });
  const [petProfile, setPetProfile] = useState<PetInterface>(petProfileData);
  let location = useLocation();

  const queryClient = useQueryClient();

  const isDetails = location.pathname.toLowerCase().includes('details');
  useDisplayViewport((breakpointsCallback: any) => {
    setBreakpoints(breakpointsCallback);
  });

  const { data: clinics, isError: clinicsError } = useQuery<Clinics, FetchError>(
    [getPetClinics.key, petProfile.id],
    getPetClinics,
    {
      retry: retryCount,
    },
  );

  useEffect(() => {
    const invalidQueries = [queryClient.removeQueries([getMicroInteraction.key])];
    Promise.all(invalidQueries);
  }, [queryClient]);

  const isMobileDetails = isDetails && !minLarge;

  return (
    <section className="pet-profile">
      <Helmet>
        <title>{petProfile.name}&apos;s Profile | Chewy.com</title>
      </Helmet>
      {isNotChewyMobileApp() && <ProfileBreadCrumbs petName={petProfile.name} />}
      {!isMobileDetails ? <Banner petProfile={petProfile} /> : null}
      <div className="pet-profile__wrapper">
        <div className="pet-profile__wrapper--columns">
          {!isMobileDetails ? (
            <Overview petProfile={petProfile} setPetProfile={setPetProfile} />
          ) : null}
          <section
            className="pet-profile__content"
            aria-label={`Curated content for ${petProfile.name}`}
          >
            <PetProfileDetails petProfile={petProfile} petPrescriptions={petPrescriptions} />
            <PetProfileClinics clinics={clinics} clinicError={clinicsError} />
            <PetViewContent petProfile={petProfile} />
          </section>
        </div>
        <div className="pet-profile-articles-container">
          <RenderArticles petProfile={petProfile} isMinLarge={minLarge} />
        </div>
        <div className="pet-profile__footer-edit">
          <EditActionChip
            petProfileId={petProfile.id}
            name={petProfile.name}
            footer={true}
            className="pet-profile__footer-edit__btn"
          />
        </div>
      </div>
    </section>
  );
};

export default PetProfileView;
